import { useApolloClient, useMutation, useQuery } from '@apollo/client';

import {
  GET_PARENT_PLAYERS, GET_PARENT_SELF, GET_PARENT_PLAYER_CONTACTS,
  GET_PARENT_ACCOUNT_CONFLICTS,
} from '../queries/parent.queries';
import {
  REQUEST_PARENT_CONTACT_CODE, VERIFY_PARENT_CONTACT,
  PARENT_UPDATE_NAME, PARENT_SET_DEFAULT_EMAIL, PARENT_SET_DEFAULT_PHONE,
  PARENT_REMOVE_EMAIL, PARENT_REMOVE_PHONE,
  PARENT_ADD_PLAYER_CONTACT, PARENT_UPDATE_PLAYER_CONTACT, PARENT_APPLY_DEFAULTS_TO_PLAYER_CONTACT,
  PARENT_DELETE_PLAYER_CONTACT,
  PARENT_REQUEST_TAKEOVER_CODE, PARENT_VERIFY_TAKEOVER,
} from '../queries/parent.mutations';
import { Reference } from '@apollo/client';
import { useLogQueryError } from '../hooks/use-log-query-error';
import { getAuthUser, parentUser } from './auth.service';
import { IParentPlayerGroup, IParentSelf, IParentPlayerContact, IParentAccountConflict } from '../types';

// HOOK: parent's player list. `cache-and-network` is load-bearing — see
// "Parent queries use cache-and-network" in CLAUDE.md.
export const useParentPlayers = () => {
  const user = getAuthUser();
  const { data, loading, error } = useQuery(GET_PARENT_PLAYERS, {
    skip: !user,
    fetchPolicy: 'cache-and-network',
  });

  useLogQueryError(error, 'parentPlayers');

  const groups: IParentPlayerGroup[] = data?.parentPlayers || [];
  return { groups, loading, error };
};

// HOOK: parent's verified credentials. `cache-and-network` is load-bearing —
// see "Parent queries use cache-and-network" in CLAUDE.md.
export const useParentSelf = () => {
  const user = getAuthUser();
  const { data, loading, error } = useQuery(GET_PARENT_SELF, {
    skip: !user,
    fetchPolicy: 'cache-and-network',
  });

  useLogQueryError(error, 'parentSelf');

  const parent: IParentSelf | undefined = data?.parentSelf;
  return { parent, loading, error };
};

// HOOK: OTP request. See "Parent OTP auth — one mutation pair" in CLAUDE.md
// for the auth-context branching.
export const useRequestParentContactCode = () =>
  useMutation(REQUEST_PARENT_CONTACT_CODE);

// HOOK: OTP verify. String-name refetch only hits *mounted* observers, so the
// login flow (no observers yet) is a no-op. See CLAUDE.md design notes.
// GetPublicPlayerHeader is listed because verifying runs the server's
// contact-claiming sweep, which the header's credentialContact derives from.
// GetParentAccountConflicts / GetParentPlayerContacts are listed because a
// verify can now TAKE OVER the address — which moves players between accounts
// and clears the conflict that prompted it.
export const useVerifyParentContact = () =>
  useMutation(VERIFY_PARENT_CONTACT, {
    refetchQueries: [
      'GetParentSelf', 'GetParentPlayers', 'GetPublicPlayerHeader',
      'GetParentPlayerContacts', 'GetParentAccountConflicts',
    ],
    awaitRefetchQueries: true,
  });

// HOOK: other parent accounts entangled with this one. Drives the switch-account
// banner; `cache-and-network` so a completed takeover clears it promptly.
export const useParentAccountConflicts = () => {
  const user = getAuthUser();
  const { data, loading, error } = useQuery(GET_PARENT_ACCOUNT_CONFLICTS, {
    skip: !user,
    fetchPolicy: 'cache-and-network',
  });

  useLogQueryError(error, 'parentAccountConflicts');

  const conflicts: IParentAccountConflict[] = data?.parentAccountConflicts || [];
  return { conflicts, loading, error };
};

// HOOKs: the takeover OTP pair. Same server path as the manual add-an-address
// flow — these exist because the client only holds the MASKED address.
export const useRequestParentTakeoverCode = () =>
  useMutation(PARENT_REQUEST_TAKEOVER_CODE);

export const useVerifyParentTakeover = () =>
  useMutation(PARENT_VERIFY_TAKEOVER, {
    refetchQueries: [
      'GetParentSelf', 'GetParentPlayers', 'GetPublicPlayerHeader',
      'GetParentPlayerContacts', 'GetParentAccountConflicts',
    ],
    awaitRefetchQueries: true,
  });

// HOOK: parent's PlayerContact rows (one per player). The account page's
// per-player override editor reads this; `cache-and-network` keeps the list
// fresh after a default-credentials cascade without flashing empty state.
export const useParentPlayerContacts = () => {
  const user = getAuthUser();
  const { data, loading, error } = useQuery(GET_PARENT_PLAYER_CONTACTS, {
    skip: !user,
    fetchPolicy: 'cache-and-network',
  });

  useLogQueryError(error, 'parentPlayerContacts');

  const contacts: IParentPlayerContact[] = data?.parentPlayerContacts || [];
  return { contacts, loading, error };
};

// HOOK: rename the parent. Server cascades to every owned PlayerContact, so
// refetch both surfaces that render a name. The `parentUser` reactive var is
// updated synchronously on success so the ParentHeader reflects the new name
// without waiting for the GetParentSelf refetch to land.
export const useParentUpdateName = () =>
  useMutation(PARENT_UPDATE_NAME, {
    refetchQueries: ['GetParentSelf', 'GetParentPlayers', 'GetParentPlayerContacts'],
    awaitRefetchQueries: true,
    onCompleted: (data) => {
      const current = parentUser();
      const nextName = data?.parentUpdateName?.name;
      if (current && nextName) parentUser({ ...current, name: nextName });
    },
  });

// HOOKs: reorder a verified credential to position 0 (= the default). Apollo
// normalizes the returned ParentSelf entity in place, so every parentSelf
// consumer updates. The `parentUser` reactive var is a separate channel —
// ParentHeader reads `email`/`phone` from it for the identity subtitle, so
// sync the new default on success (same pattern as useParentUpdateName).
export const useParentSetDefaultEmail = () =>
  useMutation(PARENT_SET_DEFAULT_EMAIL, {
    onCompleted: (data) => {
      const current = parentUser();
      const nextEmail: string | undefined = data?.parentSetDefaultEmail?.emails?.[0];
      if (current && nextEmail) parentUser({ ...current, email: nextEmail });
    },
  });
export const useParentSetDefaultPhone = () =>
  useMutation(PARENT_SET_DEFAULT_PHONE, {
    onCompleted: (data) => {
      const current = parentUser();
      const nextPhone: string | undefined = data?.parentSetDefaultPhone?.phones?.[0];
      if (current && nextPhone) parentUser({ ...current, phone: nextPhone });
    },
  });

// HOOKs: remove a verified credential. Reactive-var sync follows the same
// pattern as useParentSetDefaultEmail/Phone; empty string = "no default left".
export const useParentRemoveEmail = () =>
  useMutation(PARENT_REMOVE_EMAIL, {
    onCompleted: (data) => {
      const current = parentUser();
      const nextEmail: string = data?.parentRemoveEmail?.emails?.[0] || '';
      if (current && current.email !== nextEmail) parentUser({ ...current, email: nextEmail });
    },
  });
export const useParentRemovePhone = () =>
  useMutation(PARENT_REMOVE_PHONE, {
    onCompleted: (data) => {
      const current = parentUser();
      const nextPhone: string = data?.parentRemovePhone?.phones?.[0] || '';
      if (current && current.phone !== nextPhone) parentUser({ ...current, phone: nextPhone });
    },
  });

// HOOK: add a co-parent contact. The new row joins `otherContacts`, so refetch —
// from onCompleted, so a failed refetch can't fail an add that already committed.
export const useParentAddPlayerContact = () => {
  const client = useApolloClient();

  return useMutation(PARENT_ADD_PLAYER_CONTACT, {
    onCompleted: () => {
      client.query({ query: GET_PARENT_PLAYER_CONTACTS, fetchPolicy: 'network-only' })
        .catch((error) => console.error('parentAddPlayerContact contacts refetch failed:', error));
    },
  });
};

// HOOK: write the parent's per-contact override for one PlayerContact. The
// mutation response carries __typename + _id + every editable field, so the
// in-place cache write IS the update — never refetch this list query.
// See "Apollo — mutations refresh every affected view" in CLAUDE.md.
export const useParentUpdatePlayerContact = () => useMutation(PARENT_UPDATE_PLAYER_CONTACT);

// HOOK: stamp the parent's current defaults onto a PlayerContact. Same shape
// as parentUpdatePlayerContact — response normalises in place.
export const useParentApplyDefaultsToPlayerContact = () => useMutation(PARENT_APPLY_DEFAULTS_TO_PLAYER_CONTACT);

// HOOK: soft-delete a PlayerContact. The mutation returns only the deleted id,
// which Apollo's normaliser can't translate into a list removal — write the
// cache update by implication: drop the ref from `parentPlayerContacts` and
// evict the entity. `parentPlayers` is a separate server projection whose
// `contact` rows derive from the same PlayerContact, so refetch that one.
export const useParentDeletePlayerContact = () =>
  useMutation(PARENT_DELETE_PLAYER_CONTACT, {
    refetchQueries: ['GetParentPlayers'],
    update(cache, { data }) {
      const deletedId: string | undefined = data?.parentDeletePlayerContact;
      if (!deletedId) return;
      cache.modify({
        fields: {
          parentPlayerContacts(existing: Reference[] = [], { readField }) {
            return existing.filter((ref) => readField('_id', ref) !== deletedId);
          },
        },
      });
      cache.evict({ id: cache.identify({ __typename: 'ParentPlayerContact', _id: deletedId }) });
      cache.gc();
    },
  });
